import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { X, AlertCircle, Gauge, Clock, DollarSign, Database, ClipboardList, CheckCircle2, Loader2, Search } from "lucide-react";
import { http, errMsg, formatBytes, formatDuration } from "@/lib/api";
import { StatusPill } from "@/pages/Dashboard";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

const JOB_TABS = [
    { id: "all", label: "All", match: () => true },
    { id: "in_progress", label: "In Progress", match: (j) => ["running", "queued", "paused"].includes(j.status) },
    { id: "succeeded", label: "Completed", match: (j) => j.status === "succeeded" },
    { id: "failed", label: "Failed", match: (j) => ["failed", "completed_with_errors"].includes(j.status) },
    { id: "canceled", label: "Cancelled", match: (j) => j.status === "canceled" },
];

export default function Jobs() {
    const { user } = useAuth();
    const { subscribe } = useWS();
    const [jobs, setJobs] = useState([]);
    const [migrations, setMigrations] = useState([]);
    const [view, setView] = useState("transfers"); // transfers | migrations
    const [filter, setFilter] = useState("all");
    const [searchParams, setSearchParams] = useSearchParams();
    const showCreate = searchParams.get("create") === "1";
    const setShowCreate = (v) => {
        if (v) setSearchParams({ create: "1" });
        else setSearchParams({});
    };

    const canCreate = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;
    void canCreate;

    const refresh = async () => {
        try {
            const [j, m] = await Promise.all([
                http.get("/jobs").catch(() => ({ data: [] })),
                http.get("/migrations").catch(() => ({ data: [] })),
            ]);
            setJobs(j.data || []);
            setMigrations(m.data || []);
        } catch {
            /* ignore */
        }
    };

    // Unified filter state — drives both views from one toolbar so
    // operators can pivot between block transfers and PC migrations
    // without losing their query/status/range. Source-of-truth is the
    // URL query string so a filtered view is shareable.
    const globalQuery = (searchParams.get("q") || "").toLowerCase();
    const globalStatus = searchParams.get("status") || "all";   // all | running | succeeded | failed | queued | paused
    const globalRange = searchParams.get("range") || "all";     // all | 24h | 7d | 30d
    const setGlobal = (patch) => {
        const next = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(patch)) {
            if (!v || v === "all" || v === "") next.delete(k);
            else next.set(k, v);
        }
        setSearchParams(next);
    };
    const rangeCutoffMs = useMemo(() => {
        if (globalRange === "24h") return Date.now() - 24 * 3600_000;
        if (globalRange === "7d")  return Date.now() - 7  * 24 * 3600_000;
        if (globalRange === "30d") return Date.now() - 30 * 24 * 3600_000;
        return null;
    }, [globalRange]);

    /** Coerce the heterogeneous job/migration shapes into one normalised
     *  status bucket so the unified filter dropdown works identically
     *  across both views. Migrations don't have a `status` field — we
     *  derive it from their `phases` dict the same way `PCMigrationsTable`
     *  computes the status pill. */
    const normaliseStatus = (s) => {
        if (!s) return "queued";
        if (s === "completed") return "succeeded";
        if (s === "in-progress") return "running";
        return s;
    };
    const migrationStatus = (m) => {
        const phases = Object.values(m.phases || {});
        if (phases.some((p) => p === "failed")) return "failed";
        if (phases.some((p) => ["queued", "in-progress"].includes(p))) return "running";
        return "succeeded";
    };

    const matchesGlobal = (entity, kind) => {
        // Text query — match against name + agent names + ID prefix.
        if (globalQuery) {
            const hay = (kind === "job"
                ? `${entity.name || ""} ${entity.source_agent_name || ""} ${entity.dest_agent_name || ""} ${entity.id}`
                : `${entity.source_agent_name || ""} ${entity.id}`).toLowerCase();
            if (!hay.includes(globalQuery)) return false;
        }
        // Status filter — derive a normalised bucket per kind.
        if (globalStatus !== "all") {
            const s = kind === "job" ? normaliseStatus(entity.status) : migrationStatus(entity);
            if (s !== globalStatus) return false;
        }
        // Date range — both kinds expose `created_at` ISO timestamp.
        if (rangeCutoffMs !== null) {
            const t = entity.created_at ? Date.parse(entity.created_at) : NaN;
            if (Number.isNaN(t) || t < rangeCutoffMs) return false;
        }
        return true;
    };

    // Pre-filter both data sets so the view-switcher counts and the
    // tables both reflect the active toolbar query.
    const filteredJobs       = useMemo(() => jobs.filter(j => matchesGlobal(j, "job")),       [jobs, globalQuery, globalStatus, rangeCutoffMs]);
    const filteredMigrations = useMemo(() => migrations.filter(m => matchesGlobal(m, "mig")), [migrations, globalQuery, globalStatus, rangeCutoffMs]);

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 5000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const unsub = subscribe((m) => {
            if (["job.progress", "job.complete", "job.update", "job.created"].includes(m.event)) {
                refresh();
            }
            if (m.event && m.event.startsWith("migration.")) {
                refresh();
            }
        });
        return unsub;
    }, [subscribe]);

    // Block-transfer view drilldown — applies the per-tab status sub-filter
    // ON TOP OF the global toolbar filter so the URL filter wins. The
    // per-tab counts also reflect the global filter.
    const filtered = useMemo(() => {
        const tab = JOB_TABS.find((t) => t.id === filter) || JOB_TABS[0];
        return filteredJobs.filter((j) => tab.match(j));
    }, [filteredJobs, filter]);

    const metrics = useMemo(() => {
        const count = (pred) => filteredJobs.filter(pred).length;
        return {
            total: filteredJobs.length,
            in_progress: count((j) => ["running", "queued", "paused"].includes(j.status)),
            completed: count((j) => j.status === "succeeded"),
            failed: count((j) => ["failed", "completed_with_errors"].includes(j.status)),
        };
    }, [filteredJobs]);

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            <div className="mb-5 flex items-end justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Migrations
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Jobs</h1>
                </div>
            </div>

            {/* Unified search & filter toolbar — drives BOTH views.
                Results are shareable via ?q= ?status= ?range= URL params. */}
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="jobs-global-toolbar">
                <div className="relative flex-1 min-w-[260px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                    <input
                        placeholder="Search by name, agent, or job ID across transfers + migrations…"
                        value={globalQuery}
                        onChange={(e) => setGlobal({ q: e.target.value })}
                        data-testid="jobs-global-search"
                        className="mb-focus w-full rounded-sm border border-input bg-background pl-8 pr-3 py-1.5 text-sm"
                    />
                </div>
                <select
                    value={globalStatus}
                    onChange={(e) => setGlobal({ status: e.target.value })}
                    data-testid="jobs-global-status"
                    className="rounded-sm border border-input bg-background px-2.5 py-1.5 text-xs font-mono uppercase tracking-wider"
                >
                    <option value="all">All status</option>
                    <option value="running">Running</option>
                    <option value="queued">Queued</option>
                    <option value="paused">Paused</option>
                    <option value="succeeded">Succeeded</option>
                    <option value="failed">Failed</option>
                </select>
                <select
                    value={globalRange}
                    onChange={(e) => setGlobal({ range: e.target.value })}
                    data-testid="jobs-global-range"
                    className="rounded-sm border border-input bg-background px-2.5 py-1.5 text-xs font-mono uppercase tracking-wider"
                >
                    <option value="all">Any time</option>
                    <option value="24h">Last 24 h</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                </select>
                {(globalQuery || globalStatus !== "all" || globalRange !== "all") && (
                    <button
                        type="button"
                        onClick={() => setGlobal({ q: "", status: "all", range: "all" })}
                        data-testid="jobs-global-clear"
                        className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground px-2"
                    >
                        Clear filters
                    </button>
                )}
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    {filteredJobs.length + filteredMigrations.length} match · {jobs.length + migrations.length} total
                </span>
            </div>

            {/* Top-level view switcher: block-transfer Jobs vs PC Migrations */}
            <div className="mb-4 flex items-center gap-1 border border-border bg-surface p-1 rounded-md w-fit" data-testid="jobs-view-switcher">
                <button
                    onClick={() => setView("transfers")}
                    data-testid="jobs-view-transfers-btn"
                    className={
                        "px-3.5 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors " +
                        (view === "transfers"
                            ? "bg-[#5b5ef0] text-white"
                            : "text-muted-foreground hover:text-foreground")
                    }
                >
                    Block transfers <span className="ml-1.5 opacity-70">{filteredJobs.length}</span>
                </button>
                <button
                    onClick={() => setView("migrations")}
                    data-testid="jobs-view-migrations-btn"
                    className={
                        "px-3.5 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors " +
                        (view === "migrations"
                            ? "bg-[#5b5ef0] text-white"
                            : "text-muted-foreground hover:text-foreground")
                    }
                >
                    PC migrations <span className="ml-1.5 opacity-70">{filteredMigrations.length}</span>
                </button>
            </div>

            {view === "migrations" ? (
                <PCMigrationsTable migrations={filteredMigrations} />
            ) : (
                <>
            {/* Top metric cards */}
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="jobs-metrics">
                <JobMetric label="Total Jobs" value={metrics.total} icon={ClipboardList} color="#7c3aed" />
                <JobMetric label="In Progress" value={metrics.in_progress} icon={Loader2} color="#3b82f6" spin={metrics.in_progress > 0} />
                <JobMetric label="Completed" value={metrics.completed} icon={CheckCircle2} color="#16a34a" />
                <JobMetric label="Failed" value={metrics.failed} icon={AlertCircle} color="#dc2626" />
            </div>

            {/* Tabs */}
            <div className="mb-4 flex flex-wrap gap-1 border-b border-border" data-testid="jobs-tabs">
                {JOB_TABS.map((t) => {
                    const count = filteredJobs.filter(t.match).length;
                    const active = filter === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setFilter(t.id)}
                            data-testid={`jobs-tab-${t.id}`}
                            className={
                                "inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors " +
                                (active
                                    ? "border-[#5b5ef0] text-[#5b5ef0]"
                                    : "border-transparent text-muted-foreground hover:border-foreground/20 hover:text-foreground")
                            }
                        >
                            {t.label}
                            <span
                                className={
                                    "rounded-full px-1.5 py-0.5 text-[10px] font-bold " +
                                    (active ? "bg-[#5b5ef0]/10 text-[#5b5ef0]" : "bg-muted text-muted-foreground")
                                }
                            >
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="border border-border bg-surface">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="jobs-table">
                        <thead className="border-b border-border">
                            <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                <th className="px-4 py-2 text-left">Name</th>
                                <th className="px-4 py-2 text-left">Source → Dest</th>
                                <th className="px-4 py-2 text-left">Mode</th>
                                <th className="px-4 py-2 text-left">Status</th>
                                <th className="px-4 py-2 text-right">Files</th>
                                <th className="px-4 py-2 text-right">Size</th>
                                <th className="px-4 py-2 text-right">Speed</th>
                                <th className="px-4 py-2 text-right">Progress</th>
                                <th className="px-4 py-2 text-right">ETA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-4 py-16 text-center text-sm text-muted-foreground">
                                        No jobs match your filter.
                                    </td>
                                </tr>
                            )}
                            {filtered.map((j) => (
                                <tr key={j.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                                    <td className="px-4 py-2.5">
                                        <Link
                                            to={`/jobs/${j.id}`}
                                            className="font-medium hover:text-brand"
                                            data-testid={`jobs-row-link-${j.id}`}
                                        >
                                            {j.name}
                                        </Link>
                                        <div className="font-mono text-[10px] text-muted-foreground">
                                            {j.id.slice(0, 8)} · {j.created_by_email}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 font-mono text-[11px]">
                                        {j.source_agent_name} → {j.dest_agent_name}
                                    </td>
                                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{j.mode}</td>
                                    <td className="px-4 py-2.5">
                                        <StatusPill status={j.status} />
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                                        {j.files_done}/{j.total_files}
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                                        {formatBytes(j.total_bytes)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                                        {j.avg_speed_mbps ? `${j.avg_speed_mbps.toFixed(1)} MB/s` : "—"}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center justify-end gap-2">
                                            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-border">
                                                <div
                                                    className="h-full bg-[hsl(var(--accent))]"
                                                    style={{ width: `${Math.min(100, (j.progress || 0) * 100)}%` }}
                                                />
                                            </div>
                                            <span className="font-mono text-[11px] tabular-nums">
                                                {((j.progress || 0) * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                                        {j.status === "running" ? formatDuration(j.eta_seconds || 0) : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && <CreateJobModal onClose={() => setShowCreate(false)} onCreated={refresh} />}
                </>
            )}
        </div>
    );
}

// ----------------------- PC Migrations table ------------------------------
//
// Lightweight summary view of /api/migrations rows. The full per-job
// detail (phases, artifacts, app-status drawer, Apply CTA) lives on
// /migrations — this is the navigational hub on /jobs so operators can
// see both block-transfer and PC-migration activity in one place.

function PCMigrationsTable({ migrations }) {
    const totalApplies = (m) => (m.applies || []).length;
    return (
        <div className="border border-border bg-surface" data-testid="jobs-pc-migrations">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-border">
                        <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            <th className="px-4 py-2 text-left">Source</th>
                            <th className="px-4 py-2 text-left">Phases</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-right">Artifacts</th>
                            <th className="px-4 py-2 text-right">Created</th>
                            <th className="px-4 py-2 text-right">Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {migrations.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-4 py-16 text-center text-sm text-muted-foreground">
                                    No PC captures yet.{" "}
                                    <Link to="/migrations" className="text-brand hover:underline">
                                        Start a capture →
                                    </Link>
                                </td>
                            </tr>
                        )}
                        {migrations.map((m) => {
                            const phases = Object.entries(m.phases || {});
                            const fail = phases.some(([, s]) => s === "failed");
                            const inflight = phases.some(([, s]) => ["queued", "in-progress"].includes(s));
                            const status = fail ? "failed" : inflight ? "running" : "succeeded";
                            return (
                                <tr key={m.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30" data-testid={`pc-migration-row-${m.id}`}>
                                    <td className="px-4 py-2.5">
                                        <div className="font-medium">{m.source_agent_name || m.source_agent_id?.slice(0, 8)}</div>
                                        <div className="font-mono text-[10px] text-muted-foreground">{m.id.slice(0, 8)}</div>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex flex-wrap gap-1">
                                            {phases.map(([p, s]) => (
                                                <span
                                                    key={p}
                                                    className={
                                                        "inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] " +
                                                        (s === "completed" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                                         : s === "failed"   ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                                                         : s === "in-progress" ? "bg-sky-500/10 text-sky-400 border border-sky-500/20"
                                                         : "bg-zinc-500/10 text-zinc-400 border border-zinc-500/20")
                                                    }
                                                >
                                                    {p}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5"><StatusPill status={status} /></td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                                        {(m.artifacts || []).length}
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-[10px] text-muted-foreground">
                                        {m.created_at ? new Date(m.created_at).toLocaleString() : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <Link
                                            to="/migrations"
                                            className="text-brand hover:underline text-xs"
                                            data-testid={`pc-migration-open-${m.id}`}
                                        >
                                            Open →
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CreateJobModal({ onClose, onCreated }) {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [form, setForm] = useState({
        name: "Nightly backup migration",
        source_path: "/srv/db-backups",
        destination_path: "/mnt/dr-archive",
        source_agent_id: "",
        dest_agent_id: "",
        mode: "one_shot",
        total_files: 500,
        total_gb: 10,
    });
    const [estimate, setEstimate] = useState(null);
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        http.get("/agents").then(({ data }) => {
            setAgents(data);
            // An installed agent always registers with role="both" -- include
            // it so a real agent is preferred over an arbitrary data[0]/[1]
            // fallback (same bug/fix as Schedules.jsx's source/destination
            // pickers).
            const src = data.find((a) => a.role === "source" || a.role === "both" || !a.role);
            const dst = data.find((a) => a.role === "destination" || a.role === "both" || !a.role);
            setForm((f) => ({
                ...f,
                source_agent_id: src?.id || data[0]?.id || "",
                dest_agent_id: dst?.id || data[1]?.id || "",
            }));
        });
    }, []);

    useEffect(() => {
        const bytes = Math.max(1024, Math.floor(form.total_gb * 1024 * 1024 * 1024));
        http
            .post("/jobs/estimate", {
                total_files: form.total_files,
                total_bytes: bytes,
                mode: form.mode,
            })
            .then(({ data }) => setEstimate(data))
            .catch(() => setEstimate(null));
    }, [form.total_files, form.total_gb, form.mode]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErr("");
        try {
            const bytes = Math.max(1024, Math.floor(form.total_gb * 1024 * 1024 * 1024));
            const { data } = await http.post("/jobs", {
                name: form.name,
                source_path: form.source_path,
                destination_path: form.destination_path,
                source_agent_id: form.source_agent_id,
                dest_agent_id: form.dest_agent_id,
                mode: form.mode,
                total_files: Number(form.total_files),
                total_bytes: bytes,
            });
            onCreated();
            onClose();
            navigate(`/jobs/${data.id}`);
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            data-testid="create-job-modal"
            role="dialog"
        >
            <div className="w-full max-w-3xl border border-border bg-surface shadow-xl">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            New migration
                        </div>
                        <div className="font-display text-xl font-bold">Configure job</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-sm border border-border p-1.5 hover:border-foreground/40"
                        aria-label="Close"
                        data-testid="create-job-close"
                    >
                        <X size={14} />
                    </button>
                </div>
                <form onSubmit={submit} className="grid grid-cols-1 gap-6 p-5 md:grid-cols-2">
                    <div className="space-y-3">
                        <Field label="Job name">
                            <input
                                value={form.name}
                                onChange={set("name")}
                                required
                                data-testid="create-job-name"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Source path (on source agent)">
                            <input
                                value={form.source_path}
                                onChange={set("source_path")}
                                required
                                data-testid="create-job-source-path"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </Field>
                        <Field label="Destination path (on destination agent)">
                            <input
                                value={form.destination_path}
                                onChange={set("destination_path")}
                                required
                                data-testid="create-job-dest-path"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </Field>
                        <Field label="Source agent">
                            <select
                                value={form.source_agent_id}
                                onChange={set("source_agent_id")}
                                required
                                data-testid="create-job-source-agent"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                            >
                                {agents.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name} ({a.role}) · {a.status}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Destination agent">
                            <select
                                value={form.dest_agent_id}
                                onChange={set("dest_agent_id")}
                                required
                                data-testid="create-job-dest-agent"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                            >
                                {agents.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name} ({a.role}) · {a.status}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    <div className="space-y-3">
                        <Field label="Mode">
                            <div className="grid grid-cols-2 gap-2">
                                {["one_shot", "continuous"].map((m) => (
                                    <button
                                        type="button"
                                        key={m}
                                        onClick={() => setForm({ ...form, mode: m })}
                                        data-testid={`create-job-mode-${m}`}
                                        className={[
                                            "rounded-sm border px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors",
                                            form.mode === m
                                                ? "border-brand bg-brand text-brand-foreground"
                                                : "border-border text-muted-foreground hover:border-foreground/40",
                                        ].join(" ")}
                                    >
                                        {m.replace("_", " ")}
                                    </button>
                                ))}
                            </div>
                            <div className="mt-1.5 text-[11px] text-muted-foreground">
                                Continuous mode triggers block-level delta sync (only changed 4MB blocks transfer).
                            </div>
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Files to transfer">
                                <input
                                    type="number"
                                    min={1}
                                    value={form.total_files}
                                    onChange={set("total_files")}
                                    data-testid="create-job-files"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                                />
                            </Field>
                            <Field label="Total size (GB)">
                                <input
                                    type="number"
                                    step="0.1"
                                    min={0.001}
                                    value={form.total_gb}
                                    onChange={set("total_gb")}
                                    data-testid="create-job-gb"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                                />
                            </Field>
                        </div>

                        <div className="border border-dashed border-border bg-background p-3">
                            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Cost estimate
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <EstimateRow
                                    icon={Gauge}
                                    label="Effective transfer"
                                    value={formatBytes(estimate?.effective_bytes || 0)}
                                />
                                <EstimateRow
                                    icon={Clock}
                                    label="Est. duration"
                                    value={formatDuration(estimate?.estimated_seconds || 0)}
                                />
                                <EstimateRow
                                    icon={Database}
                                    label="Delta ratio"
                                    value={estimate ? `${(estimate.delta_ratio * 100).toFixed(0)}%` : "—"}
                                />
                                <EstimateRow
                                    icon={DollarSign}
                                    label="Est. cost"
                                    value={estimate ? `$${estimate.estimated_cost_usd.toFixed(2)}` : "—"}
                                />
                            </div>
                        </div>

                        {err && (
                            <div
                                data-testid="create-job-error"
                                className="flex items-center gap-2 rounded-sm border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]"
                            >
                                <AlertCircle size={13} /> {err}
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-sm border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="create-job-submit"
                                className="rounded-sm bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-widest text-brand-foreground hover:bg-brand-dark disabled:opacity-60"
                            >
                                {submitting ? "Queuing…" : "Queue migration"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {label}
            </span>
            {children}
        </label>
    );
}

function EstimateRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center justify-between rounded-sm border border-border bg-surface px-2.5 py-2">
            <div className="flex items-center gap-1.5 text-muted-foreground">
                <Icon size={12} />
                <span className="text-[11px]">{label}</span>
            </div>
            <span className="font-mono text-xs font-semibold">{value}</span>
        </div>
    );
}

function JobMetric({ label, value, icon: Icon, color, spin }) {
    return (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4">
            <div
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ background: `${color}1a`, color }}
            >
                <Icon size={18} className={spin ? "animate-spin" : ""} />
            </div>
            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {label}
                </div>
                <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
            </div>
        </div>
    );
}
